import { useCallback, useState, type Dispatch, type SetStateAction } from 'react'

export function homePanelStorageKey(
  userId: string | undefined,
  preference: string,
): string {
  return `berky.home.${userId ?? 'anonymous'}.${preference}`
}

export function useRememberedDisclosure(
  storageKey: string,
  defaultExpanded = true,
): [boolean, Dispatch<SetStateAction<boolean>>] {
  const [expanded, setExpandedState] = useState(() =>
    readBoolean(storageKey, defaultExpanded),
  )

  const setExpanded = useCallback<Dispatch<SetStateAction<boolean>>>(
    (nextValue) => {
      setExpandedState((current) => {
        const next = typeof nextValue === 'function' ? nextValue(current) : nextValue
        writeStorage(storageKey, String(next))
        return next
      })
    },
    [storageKey],
  )

  return [expanded, setExpanded]
}

export function readStorage(storageKey: string): string | null {
  try {
    return window.localStorage.getItem(storageKey)
  } catch {
    return null
  }
}

export function writeStorage(storageKey: string, value: string): void {
  try {
    window.localStorage.setItem(storageKey, value)
  } catch {
    // The home page still works when the browser blocks local storage.
  }
}

function readBoolean(storageKey: string, fallback: boolean): boolean {
  const stored = readStorage(storageKey)
  if (stored === 'true') return true
  if (stored === 'false') return false
  return fallback
}
